export default {
  // Common
  appName: 'MeetTix',
  loading: 'Loading...',
  error: 'An error occurred. Please try again.',
  authError: 'Authentication failed. Please check your credentials and try again.',
  
  // Navigation
  nav: {
    home: 'Home',
    events: 'Events',
    pastEvents: 'Past Events',
    login: 'Login',
    register: 'Register',
    profile: 'Profile',
    myTickets: 'My Tickets',
    adminDashboard: 'Admin Dashboard',
    signOut: 'Sign out'
  },
  
  // Home Page
  home: {
    hero: {
      title: 'Find and Book Amazing Events',
      subtitle: 'Discover concerts, sports, theater, and more in your city. Get tickets to the events you love!'
    },
    featuredEvents: 'Featured Events',
    eventsInCity: 'Events in {{city}}',
    noUpcomingEvents: 'No upcoming events in {{city}}',
    viewAll: 'View All',
    browseByCategory: 'Browse by Category',
    howItWorks: 'How It Works',
    howItWorksSteps: {
      search: {
        title: 'Search Events',
        description: 'Find events by location, category, or date that match your interests.'
      },
      book: {
        title: 'Book Tickets',
        description: 'Secure your spot with our easy and safe booking process.'
      },
      attend: {
        title: 'Attend Event',
        description: 'Get ready to enjoy an unforgettable experience at your chosen event.'
      }
    },
    cta: {
      title: 'Ready to discover amazing events?',
      subtitle: 'Join thousands of people who use MeetTix to find and book tickets to the best events.',
      button: 'Sign Up Now'
    }
  },
  
  // Events Page
  events: {
    title: 'Browse Events',
    eventsFound: '{{count}} event found',
    eventsFound_plural: '{{count}} events found',
    noEvents: 'No events found matching your criteria.',
    resetFilters: 'Reset Filters',
    viewDetails: 'View Details'
  },
  
  // Event Detail Page
  eventDetail: {
    aboutEvent: 'About This Event',
    venueInfo: 'Venue Information',
    getTickets: 'Get Tickets',
    price: 'Price:',
    quantity: 'Quantity',
    ticketsAvailable: '{{count}} ticket available',
    ticketsAvailable_plural: '{{count}} tickets available',
    buyTickets: 'Buy Tickets',
    loginToBuy: 'Login to Buy Tickets',
    soldOut: 'Sold Out',
    secureCheckout: 'Secure checkout • Instant confirmation',
    notFound: 'Event not found.',
    buyTicketsExternal: 'Buy Tickets on Official Website',
    checkIn: {
      title: 'Check-ins ({{count}})',
      button: 'Check In',
      alreadyCheckedIn: 'Already Checked In',
      processing: 'Checking In...',
      error: 'Failed to check in. Please try again.',
      commentPlaceholder: 'Share your thoughts about the event...',
      writeComment: 'Write a comment...'
    }
  },
  
  // Search Bar
  search: {
    city: 'City',
    allCities: 'All Cities',
    eventType: 'Event Type',
    allCategories: 'All Categories',
    date: 'Date',
    search: 'Search'
  },
  
  // Categories
  categories: {
    concert: 'Concerts',
    sports: 'Sports',
    theater: 'Theater',
    conference: 'Conferences',
    festival: 'Festivals',
    other: 'Other'
  },
  
  // Auth Pages
  auth: {
    login: {
      title: 'Sign in to your account',
      noAccount: 'Or create a new account',
      email: 'Email address',
      password: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot your password?',
      signIn: 'Sign in',
      signingIn: 'Signing in...',
      continueWith: 'Or continue with',
      google: 'Google'
    },
    register: {
      title: 'Create your account',
      haveAccount: 'Or sign in to your existing account',
      name: 'Full name',
      email: 'Email address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      terms: 'I agree to the Terms of Service and Privacy Policy',
      createAccount: 'Create Account',
      creatingAccount: 'Creating account...',
      continueWith: 'Or continue with',
      google: 'Google'
    },
    resetPassword: {
      title: 'Reset Password',
      email: 'Email address',
      send: 'Send Reset Link',
      sending: 'Sending...',
      backToLogin: 'Back to Login',
      success: 'Password reset email sent! Check your inbox.',
      error: 'Failed to send reset email. Please try again.'
    },
    errors: {
      invalidEmail: 'Invalid email address.',
      userDisabled: 'This account has been disabled.',
      invalidCredential: 'Incorrect email or password.',
      tooManyRequests: 'Too many attempts. Please try again in a few minutes.',
      network: 'Network error. Check your internet connection and try again.',
      operationNotAllowed: 'Email/password sign-in is not enabled. Please contact support.',
      emailInUse: 'This email is already registered.',
      weakPassword: 'Password must be at least 6 characters.',
      popupClosed: 'Sign-in window was closed before completing.'
    }
  },

  // Profile Page
  profile: {
    title: 'Profile',
    fields: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      phoneNotSet: 'Not set',
      enterPhone: 'Enter your phone number'
    },
    buttons: {
      edit: 'Edit Profile',
      save: 'Save Changes',
      saving: 'Saving...',
      cancel: 'Cancel',
      signOut: 'Sign Out'
    },
    errors: {
      updateFailed: 'Failed to update profile',
      photoUploadFailed: 'Failed to upload photo'
    }
  },
  
  // Admin
  admin: {
    dashboard: {
      title: 'Admin Dashboard',
      addEvent: 'Add New Event',
      noEvents: 'No events found.',
      createFirst: 'Create Your First Event',
      event: 'Event',
      dateTime: 'Date & Time',
      location: 'Location',
      price: 'Price',
      tickets: 'Tickets',
      actions: 'Actions',
      confirmDelete: 'Confirm delete?',
      yes: 'Yes',
      no: 'No'
    },
    eventForm: {
      createEvent: 'Create New Event',
      editEvent: 'Edit Event',
      back: 'Back to Dashboard',
      eventTitle: 'Event Title',
      description: 'Description',
      venue: 'Venue',
      city: 'City',
      selectCity: 'Select a city',
      date: 'Date',
      time: 'Time',
      category: 'Category',
      selectCategory: 'Select a category',
      price: 'Price ($)',
      availableTickets: 'Available Tickets',
      ticketUrl: 'Ticket Purchase URL',
      ticketUrlPlaceholder: 'https://example.com/buy-tickets',
      ticketUrlHelp: 'External website where users can purchase tickets',
      eventImage: 'Event Image',
      uploadImage: 'Upload image',
      imageTypes: 'PNG, JPG, GIF up to 10MB',
      cancel: 'Cancel',
      update: 'Update Event',
      create: 'Create Event',
      updating: 'Updating...',
      creating: 'Creating...',
      fillAllFields: 'Please fill all required fields'
    }
  },
  
  // Footer
  footer: {
    description: 'Find and book tickets for the best events in your city. Concerts, sports, theater and more.',
    quickLinks: 'Quick Links',
    categories: 'Categories',
    contactUs: 'Contact Us',
    newsletter: {
      title: 'Subscribe to our newsletter',
      placeholder: 'Your email',
      button: 'Subscribe'
    },
    copyright: '© {{year}} MeetTix. All rights reserved.',
    policies: {
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy'
    }
  }
};